import {
    BASE_CONSTANTS,
    IService,
    ITCIMarketplaceChangeNotification,
    ITCIMarketplaceListing,
    ITCIMarketplaceListingAccess,
    ITCIMarketplaceListingMetaData,
    ITCIMarketplaceOrgSubscriptionLocatorResponse,
    ITCIMarketplaceV2AccessResponse,
    ITCIMarketplaceV2ActionRequest,
    ITCIMarketplaceV2ActionResponse,
    ITCIMarketplaceV2ListingCallbackResponse,
    ITCIMarketplaceV2ListingResponse,
    ITCIMarketplaceV2ListingService,
    ITCIMediaAsset,
    ITCITenantComponentInput,
    ITCITenantComponentOptions,
    mapIServiceToTenantAppType,
    SERVICE_URLS,
    TCI_LISTING_METADATA_CALLBACK,
    TCI_LISTING_UPSERT_MODE,
    TCI_MARKETPLACE_ACTIONS,
    TCI_MARKETPLACE_NOTIFICATION_TYPE,
    TCI_MARKETPLACE_ORG_TYPE,
    TCI_MEDIA_FIELD_NAME,
    TCIMarketplaceListingStatus,
    TCITenantAppCategory,
    TCITenantAppType,
    USER_ROLES, USER_SUBSCRIPTION_TRIAL
} from "@tci/common";
import { TciTenantService } from "../ng-tenant/tci-tenant.service";
import { TCITenantRemoteServices } from "../ng-tenant/tci-tenant-remote-services";
import { TciTenantModelService } from "../ng-tenant/tci-tenant.model";
import { NgUserService } from "../ng-shared/ng-services/ng-user.service";
import { BehaviorSubject, Observable, Observer, Subject } from "rxjs";
import { Http, Response, ResponseContentType } from "@angular/http";
import { toString } from "lodash";
import * as LRUCache from "lru-cache";

const genericHttpError = (e: any) => {
    try {
        e.json(); // It will fail if no body is present in error
        return e;
    } catch (e) { }

    return {
        json: (): ITCIMarketplaceV2ListingResponse => ({
            data: [],
            success: false,
            msg: toString(e),
            error: {
                message: toString(e),
                statusMessage: toString(e)
            }
        })
    };
};

export class NgMarketplaceV2Service implements ITCIMarketplaceV2ListingService {
    private static instance: NgMarketplaceV2Service = null;

    private availableTenantServices: IService[];
    private listingUpdate$: Subject<ITCIMarketplaceChangeNotification<any>> = new Subject();
    private refreshMiniList$: BehaviorSubject<boolean> = new BehaviorSubject(false);
    private creatingAppFromMiniList$: Subject<boolean> = new Subject();
    private selectedListing$: BehaviorSubject<ITCIMarketplaceListing> = new BehaviorSubject(null);
    private createOrUpgrade = true;
    // Listing get by ID shared Observables (key is listing id)
    private listingAssetPerIdObs: { [key: string]: Observable<ITCIMarketplaceV2ListingResponse> } = {};

    // Store "getListingByAssetId" response for 2s - IPAS-17372
    private listingCreatorAssetStore: LRUCache = new LRUCache({ maxAge: 1000 });

    /**
     * getInstance
     * @param tenantRemoteServices
     * @param tenantSvc
     * @param tenantModelSvc
     * @param userSvc
     */
    static getInstance(tenantRemoteServices: TCITenantRemoteServices,
        tenantSvc: TciTenantService,
        tenantModelSvc: TciTenantModelService,
        userSvc: NgUserService,
        http: Http) {
        if (!NgMarketplaceV2Service.instance) {
            NgMarketplaceV2Service.instance = new NgMarketplaceV2Service(tenantRemoteServices, tenantSvc, tenantModelSvc, userSvc, http);
        }
        return NgMarketplaceV2Service.instance;
    }

    /**
     * constructor
     * @param tenantRemoteServices
     * @param tenantSvc
     * @param tenantModelSvc
     * @param userSvc
     */
    constructor(
        private tenantRemoteServices: TCITenantRemoteServices,
        private tenantSvc: TciTenantService,
        private tenantModelSvc: TciTenantModelService,
        private userSvc: NgUserService,
        private http: Http
    ) {
    }


    /**
     * Get All Listings from backend and match with available tenants
     * to ensure listings can be used when the associated tenant is
     * available
     */
    getAllListings(): Observable<ITCIMarketplaceListing[]> {
        const listingObs = this.http.get(SERVICE_URLS.MARKETPLACE)
            .catch(e => {
                console.error("Marketplace: eror getting all listings", e);
                return Observable.of(genericHttpError(e));
            })
            .map((response: Response) => <ITCIMarketplaceV2ListingResponse>response.json())
            .map(res => this.mapTenantDetails(res))
            // check if the response.error exists
            .map(res => {
                if (res.error) {
                    // do something with the error
                    res.data = [];
                }
                return res.data;
            });

        const tenantsObs = this.tenantRemoteServices.getRemoteServices()
            .flatMap(svcs => {
                this.availableTenantServices = svcs; // Store current tenant services
                return svcs;
            })
            .map(svc => mapIServiceToTenantAppType(svc))
            .distinct(val => val.toString())
            .toArray();

        // Execute both requests at the same time

        return Observable.combineLatest(listingObs, tenantsObs, (listingData, tenantTypes) => ({
            listingData: listingData,
            tenantTypes: tenantTypes
        }))
            .map(data => {
                // Note: support for accelerator listing item, because it's app type is unknown
                data.tenantTypes.push(TCITenantAppType.unknown);
                return data;
             })
            .map(data => data.listingData
                .filter(listing => data.tenantTypes.some(srvTenantType => srvTenantType.equals(listing.tenantType)))
            );
    }

    /**
     * Get Listing by Id from Backend
     * @param id
     */
    getListingById(id: string): Observable<ITCIMarketplaceV2ListingResponse> {
        return this.http.get(SERVICE_URLS.MARKETPLACE + "/" + id)
            .catch(e => {
                console.error("Marketplace: eror getting listing", id, e);
                return Observable.of(genericHttpError(e));
            })
            .map((response: Response) => <ITCIMarketplaceV2ListingResponse>response.json())
            .map(res => this.mapTenantDetails<ITCIMarketplaceV2ListingResponse>(res))
            .share(); // returns array
    }

    /**
     * Get Listing by Id from Backend
     * @param id
     */
    getListingByAssetId(id: string): Observable<ITCIMarketplaceV2ListingResponse> {
        const cache = this.listingCreatorAssetStore.get(id);

        if (cache) {
            return Observable.of(cache);
        }

        if (!this.listingAssetPerIdObs[id]) {
            this.listingAssetPerIdObs[id] = this.http.get(`${SERVICE_URLS.MARKETPLACE}?creatorAssetId=${id}`)
                .catch(e => {
                    console.error("Marketplace: eror getting listing asset", id, e);
                    return Observable.of(genericHttpError(e));
                })
                .map((response: Response) => <ITCIMarketplaceV2ListingResponse>response.json())
                .map(res => {
                    const data = this.mapTenantDetails<ITCIMarketplaceV2ListingResponse>(res);
                    this.listingCreatorAssetStore.set(id, data);
                    return data;
                })
                .do(() => delete this.listingAssetPerIdObs[id])
                .share(); // returns array
        }

        return this.listingAssetPerIdObs[id];
    }

    private mapTenantDetails<T extends { data: ITCIMarketplaceListing[] }>(res: T): T {
        if (Array.isArray(res.data)) {
            res.data.map(r => {
                return this.mapTenantDetail(r);
                // r.tenantType = TCITenantAppType.fromJSON(r.tenantType);
                // r.tenantCategory = TCITenantAppCategory.fromMarketplaceListing(r);
                // (Object.keys(r.listingStatus) || [])
                //     .forEach(i => r.listingStatus[i] = r.listingStatus[i].map(st => TCIMarketplaceListingStatus.fromJSON(st)));
                // (Object.entries(r.listingOrgTypeStatusMap) || [])
                //     .forEach(([orgType, status]) =>
                //         r.listingOrgTypeStatusMap[orgType] = status ? TCIMarketplaceListingStatus.fromJSON(status) : null);
                // return r;
            });
        }
        return res;
    }

    private mapTenantDetail(r: ITCIMarketplaceListing): ITCIMarketplaceListing {
        r.tenantType = TCITenantAppType.fromJSON(r.tenantType);
        r.tenantCategory = TCITenantAppCategory.fromMarketplaceListing(r);
        (Object.keys(r.listingStatus) || [])
            .forEach(i => r.listingStatus[i] = r.listingStatus[i].map(st => TCIMarketplaceListingStatus.fromJSON(st)));
        (Object.entries(r.listingOrgTypeStatusMap) || [])
            .forEach(([orgType, status]) =>
                r.listingOrgTypeStatusMap[orgType] = status ? TCIMarketplaceListingStatus.fromJSON(status) : null);
        return r;
    }

    /**
     * return true if the listing is in public status
     * @param metadata
     */
    isListingPublic(metadata: ITCIMarketplaceListingMetaData | ITCIMarketplaceListing, isMetadata: boolean = true): Observable<boolean> {
        return this.isListingStatusForOrgType(metadata,
            TCI_MARKETPLACE_ORG_TYPE.Provider,
            TCIMarketplaceListingStatus.PUBLICLY_AVAILABLE,
            isMetadata);
        // const getListingObs = isMetadata ? this.getListingByAssetId((metadata as ITCIMarketplaceListingMetaData).creatorAssetId)
        //     : this.getListingByAssetId((metadata as ITCIMarketplaceListing).id);
        // return getListingObs
        //     .map(res => {
        //         if (res.data && res.data.length > 0) {
        //             const providerGsbc = res.data[0].listingOrgMap[TCI_MARKETPLACE_ORG_TYPE.Provider][0];
        //             return res.data[0].listingStatus[providerGsbc]
        //                 .map(s => s.toString())
        //                 .includes(TCIMarketplaceListingStatus.PUBLICLY_AVAILABLE.toString());
        //         } else {
        //             return false;
        //         }
        //     });
    }

    /**
     * return true if the listing status is present for the org type
     * @param metadata
     * @param orgType
     * @param orgStatus
     * @param isMetadata
     */
    isListingStatusForOrgType(metadata: ITCIMarketplaceListingMetaData | ITCIMarketplaceListing,
        orgType: TCI_MARKETPLACE_ORG_TYPE,
        orgStatus: TCIMarketplaceListingStatus,
        isMetadata: boolean = true): Observable<boolean> {
        const thisObj = this;
        const getListingObs = isMetadata ? this.getListingByAssetId((metadata as ITCIMarketplaceListingMetaData).creatorAssetId)
            : this.getListingById((metadata as ITCIMarketplaceListing).id);
        return getListingObs
            // .map(res => thisObj.mapTenantDetails(res))
            .map(res => {
                if (res.data && res.data.length > 0) {
                    const status = res.data[0].listingOrgTypeStatusMap[orgType];
                    return status.equals(orgStatus);
                } else {
                    return false;
                }
            });
    }

    /**
     * Is listing from the logged in user Org
     * @param metadata
     * @param isMetadata
     */
    isListingFromUserOrg(metadata: ITCIMarketplaceListingMetaData | ITCIMarketplaceListing, isMetadata: boolean = true)
        : Observable<boolean> {
        const assetObs = isMetadata ? this.getListingByAssetId((metadata as ITCIMarketplaceListingMetaData).creatorAssetId)
            : this.getListingByAssetId((metadata as ITCIMarketplaceListing).id);
        const userInfoObs = this.userSvc.getUserInfo();
        return Observable.combineLatest(assetObs, userInfoObs, (asset, userinfo) => ({ asset, userinfo }))
            .map(res => {
                if (res.asset.data && res.asset.data.length > 0) {
                    const providerGsbc = res.asset.data[0].listingOrgMap[TCI_MARKETPLACE_ORG_TYPE.Provider][0];
                    return res.userinfo.gsbc === providerGsbc;
                } else {
                    return false;
                }
            });
    }

    /**
     * This is an explicit call to trigger a listing metadata refresh  which is expected
     * to do something that changes the marketplace listing menu items
     * @param input
     * @param callback
     */
    pushListingChanges(
        input: ITCIMarketplaceListingMetaData,
        callback: TCI_LISTING_METADATA_CALLBACK
    ): Observable<ITCIMarketplaceV2ListingCallbackResponse> {
        return callback(input) // Execute the callback function
            .finally(() => {
                const notification = {
                    /**
                     * Type of notification
                     */
                    type: TCI_MARKETPLACE_NOTIFICATION_TYPE.Refresh,

                    /**
                     * Listing asset that have changed
                     */
                    listing: input
                } as ITCIMarketplaceChangeNotification<ITCIMarketplaceListingMetaData>;
                this.notifyChange(notification);
            });
        // return Observable.create((obs: Observer<ITCIMarketplaceV2ListingCallbackResponse>) => {
        //
        //     callback(input) // Execute the callback function
        //         .finally(() => {
        //             const notification = {
        //                 /**
        //                  * Type of notification
        //                  */
        //                 type: TCI_MARKETPLACE_NOTIFICATION_TYPE.Refresh,
        //
        //                 /**
        //                  * Listing asset that have changed
        //                  */
        //                 listing: input
        //             } as ITCIMarketplaceChangeNotification<ITCIMarketplaceListingMetaData>;
        //             this.notifyChange(notification);
        //         })
        //         .subscribe(
        //             cbRes => {
        //                 obs.next(cbRes); // Send callback response
        //             },
        //             error => obs.error(error)
        //         );
        // });
    }


    // tslint:disable:max-line-length
    processAction(req: ITCIMarketplaceV2ActionRequest, body: any = {}): Observable<ITCIMarketplaceV2ActionResponse> {
        let obsRef: Observable<Response>;
        switch (req.actionType) {
            case TCI_MARKETPLACE_ACTIONS.GetAccess:
                obsRef = this.http.post(`${SERVICE_URLS.MARKETPLACE}/${req.listing.id}${BASE_CONSTANTS.ACTIONS}?actionType=${req.actionType}`, body);
                break;
            case TCI_MARKETPLACE_ACTIONS.RequestAccess:
                obsRef = this.http.post(`${SERVICE_URLS.MARKETPLACE}/${req.listing.id}${BASE_CONSTANTS.ACTIONS}?actionType=${req.actionType}`, body);
                break;
            case TCI_MARKETPLACE_ACTIONS.RevokeAccess:
                obsRef = this.http.post(`${SERVICE_URLS.MARKETPLACE}/${req.listing.id}${BASE_CONSTANTS.ACTIONS}?actionType=${req.actionType}&subscriptionLocator=${req.subscriptionLocator}`
                    + (req.scribeOrgId ? "&scribeOrgId=" + req.scribeOrgId : ""), body);
                break;
            case TCI_MARKETPLACE_ACTIONS.UnRevokeAccess:
                obsRef = this.http.post(`${SERVICE_URLS.MARKETPLACE}/${req.listing.id}${BASE_CONSTANTS.ACTIONS}?actionType=${req.actionType}&subscriptionLocator=${req.subscriptionLocator}`, body);
                break;
            case TCI_MARKETPLACE_ACTIONS.AcknowledgeRevokeAccess:
                obsRef = this.http.post(`${SERVICE_URLS.MARKETPLACE}/${req.listing.id}${BASE_CONSTANTS.ACTIONS}?actionType=${req.actionType}`, body);
                break;
            case TCI_MARKETPLACE_ACTIONS.AcknowledgeReject:
                obsRef = this.http.post(`${SERVICE_URLS.MARKETPLACE}/${req.listing.id}${BASE_CONSTANTS.ACTIONS}?actionType=${req.actionType}`, body);
                break;
            case TCI_MARKETPLACE_ACTIONS.Install:
                obsRef = this.http.post(`${SERVICE_URLS.MARKETPLACE}/${req.listing.id}${BASE_CONSTANTS.ACTIONS}?actionType=${req.actionType}`, body);
                break;
            case TCI_MARKETPLACE_ACTIONS.Uninstall:
                obsRef = this.http.post(`${SERVICE_URLS.MARKETPLACE}/${req.listing.id}${BASE_CONSTANTS.ACTIONS}?actionType=${req.actionType}`, body);
                break;
            case TCI_MARKETPLACE_ACTIONS.ApproveRequest:
                obsRef = this.http.post(`${SERVICE_URLS.MARKETPLACE}/${req.listing.id}${BASE_CONSTANTS.ACTIONS}?actionType=${req.actionType}&subscriptionLocator=${req.subscriptionLocator}`, body);
                break;
            case TCI_MARKETPLACE_ACTIONS.RejectRequest:
                obsRef = this.http.post(`${SERVICE_URLS.MARKETPLACE}/${req.listing.id}${BASE_CONSTANTS.ACTIONS}?actionType=${req.actionType}&subscriptionLocator=${req.subscriptionLocator}`, body);
                break;
            case TCI_MARKETPLACE_ACTIONS.WithdrawListing:
                obsRef = this.http.post(`${SERVICE_URLS.MARKETPLACE}/${req.listing.id}${BASE_CONSTANTS.ACTIONS}?actionType=${req.actionType}`, body);
                break;
            case TCI_MARKETPLACE_ACTIONS.SubmitForApproval:
                obsRef = this.http.post(`${SERVICE_URLS.MARKETPLACE}/${req.listing.id}${BASE_CONSTANTS.ACTIONS}?actionType=${req.actionType}`, body);
                break;
            case TCI_MARKETPLACE_ACTIONS.PromoteVisibilityPublic:
                obsRef = this.http.post(`${SERVICE_URLS.MARKETPLACE}/${req.listing.id}${BASE_CONSTANTS.ACTIONS}?actionType=${req.actionType}`, body);
                break;
            case TCI_MARKETPLACE_ACTIONS.PromoteVisibilityPrivate:
                obsRef = this.http.post(`${SERVICE_URLS.MARKETPLACE}/${req.listing.id}${BASE_CONSTANTS.ACTIONS}?actionType=${req.actionType}`, body);
                break;
            case TCI_MARKETPLACE_ACTIONS.DeleteListing:
                obsRef = this.http.delete(`${SERVICE_URLS.MARKETPLACE}/${req.listing.id}`);
                break;
        }

        if (obsRef) {
            return obsRef
                .catch(e => {
                    console.error("Marketplace: action request error", e);
                    return Observable.of(genericHttpError(e));
                })
                .map(res => res.json() as ITCIMarketplaceV2ActionResponse)
                .map(res => this.mapTenantDetails<ITCIMarketplaceV2ActionResponse>(res));
        } else {
            return Observable.of(<any>{
                msg: "Unknown Action",
                success: false
            } as ITCIMarketplaceV2ActionResponse);
        }

    }


    /**
     * Upsert Listing
     * @param listingData
     * @param upsertMode
     * @param listingIcon
     * @param medias
     */
    upsertListing(
        listingData: ITCIMarketplaceListing,
        upsertMode: TCI_LISTING_UPSERT_MODE,
        listingIcon?: string,
        medias?: ITCIMediaAsset[]):
        Observable<ITCIMarketplaceV2ListingResponse> {
        const mediaAssets: ITCIMediaAsset[] = [];
        const requestData = new FormData();
        // Note: need to remove custom icon base64 content from listingData
        listingData.iconContent = "";
        if (listingIcon) {
            requestData.append(TCI_MEDIA_FIELD_NAME.LISTING_ICON, this.dataUrlToFile(TCI_MEDIA_FIELD_NAME.LISTING_ICON, listingIcon));
        }
        if (medias && medias.length) {
            medias.forEach(item => {
                const isImage = item.mimeType.includes("image");
                if (item.fieldName && item.content && isImage) {
                    requestData.append(item.fieldName, this.dataUrlToFile(item.fieldName, item.content));
                }
                mediaAssets.push({
                    fieldName: item.fieldName,
                    mediaUrl: !isImage ? item.mediaUrl : "",
                });
            });
        }
        listingData.mediaAssets = mediaAssets;
        requestData.append("listingData", JSON.stringify(listingData));

        return this.http.post(`${SERVICE_URLS.MARKETPLACE}?upsertMode=${upsertMode}`, requestData)
            .catch(e => {
                console.error("Marketplace: upsert error", e);
                return Observable.of(genericHttpError(e));
            })
            .map((response: Response) => {
                return <ITCIMarketplaceV2ListingResponse>response.json();
            })
            .map(res => this.mapTenantDetails<ITCIMarketplaceV2ListingResponse>(res));
    }

    dataUrlToFile(fieldName: string, dataUrl: string): File {
        const arr = dataUrl.split(",");
        const type = arr[0].match(/:(.*?);/)[1];
        const byteString = atob(arr[1]);
        const byteStringLen = byteString.length;
        const arrayBuffer = new ArrayBuffer(byteStringLen);
        const intArr = new Uint8Array(arrayBuffer);

        for (let i = 0; i < byteStringLen; i++) {
            intArr[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([intArr], { type });
        return new File([blob], fieldName, { type });
    }


    getMarketplaceTags(): Observable<string[]> {
        return this.http.get(SERVICE_URLS.MARKETPLACE_TAGS)
            .catch(e => {
                console.error("Marketplace: tags getting error", e);
                return Observable.of({ json: () => ([]) });
            })
            .map((response: Response) => {
                return <string[]>response.json();
            });
    }

    getOrgBySubscriptionLocatorId(subscriptionLocatorId: string): Observable<ITCIMarketplaceOrgSubscriptionLocatorResponse> {
        return this.http.get(`${SERVICE_URLS.MARKETPLACE_ORGS}/${subscriptionLocatorId}`)
            .catch(e => {
                console.error("Marketplace: get org by subs locator id error", subscriptionLocatorId, e);
                return Observable.of(null);
            })
            .map((response: Response) => {
                return response ? response.json() as ITCIMarketplaceOrgSubscriptionLocatorResponse : null;
            });
    }

    // Notification methods
    getNotificationSubject(): Subject<ITCIMarketplaceChangeNotification<ITCIMarketplaceListingMetaData | ITCIMarketplaceListing>> {
        return this.listingUpdate$;
    }

    notifyChange(change: ITCIMarketplaceChangeNotification<ITCIMarketplaceListingMetaData | ITCIMarketplaceListing>) {
        if (change.listing) {
            this.listingUpdate$.next(change);
        } else {
            console.error("Marketplace: notifyChange did not include listing information");
        }
    }

    getSelectedListingSubject(): Subject<ITCIMarketplaceListing> {
        return this.selectedListing$;
    }

    getRefreshMiniListSubject(): Subject<boolean> {
        return this.refreshMiniList$;
    }

    refreshMiniListSubject(bool: boolean): void {
        return this.refreshMiniList$.next(bool);
    }

    getCreatingAppFromMiniListSubject(): Subject<boolean> {
        return this.creatingAppFromMiniList$;
    }

    creatingAppFromMiniListSubject(bool: boolean): void {
        return this.creatingAppFromMiniList$.next(bool);
    }

    selectListing(listing: ITCIMarketplaceListing): void {
        this.selectedListing$.next(listing);
    }

    getListingAccessList(id: string): Observable<ITCIMarketplaceListingAccess[]> {
        return this.http.get(`${SERVICE_URLS.MARKETPLACE}/${id}${BASE_CONSTANTS.ACCESS}`)
            .catch(e => {
                console.error("Marketplace: eror getting listing access list", e);
                return Observable.of(genericHttpError(e));
            })
            .map((response: Response) => <ITCIMarketplaceV2AccessResponse>response.json())
            .map(res => {
                if (res.error) {
                    res.data = [];
                }
                return res.data;
            })
            .map((res: ITCIMarketplaceListingAccess[]) => {
                res.forEach(r => {
                    r.consumingOrgStatus = TCIMarketplaceListingStatus.fromJSON(r.consumingOrgStatus);
                });
                return res;
            });
    }

    /**
     * Check if the asset listing can be created
     * menuInput cannot be used for extensions as it is tenant specific menu
     * tenant must provide metadata to evaluate version comparision as TCI
     * application does not have version
     * @param opt
     * @param menuInput
     * @param metaData
     */
    canCreateComponentFunction(opt: ITCITenantComponentOptions<any>,
        menuInput: ITCITenantComponentInput,
        metaData: ITCIMarketplaceListingMetaData,
        checkPendingApprovalState = true // For allow PreloadMap check on PendingApproval
    ): Observable<boolean> {
        const assetObs = this.getListingByAssetId(metaData.creatorAssetId);
        const userInfoObs = this.userSvc.getUserInfo();
        const pendingApprovalObs: Observable<boolean> = checkPendingApprovalState ?
            this.isListingStatusForOrgType(
                metaData,
                TCI_MARKETPLACE_ORG_TYPE.Provider,
                TCIMarketplaceListingStatus.PENDING_APPROVAL,
                true) :
            Observable.of(false);

        return Observable.combineLatest(assetObs, userInfoObs, pendingApprovalObs, (assetData, userinfo, isPendingApproval) => ({
            assetData,
            userinfo,
            isPendingApproval
        }))
            .map(res => {
                if (res.assetData.data.length === 0) {
                    const userIsAdmin = res.userinfo.rolX[0] === USER_ROLES.ADM; // only admin can update
                    const userIsRO = res.userinfo.rolX[0] === USER_ROLES.RO; // RO cannot edit
                    const userIsTrial = res.userinfo.st === USER_SUBSCRIPTION_TRIAL; // user is a trial user
                    // user role is NOT RO
                    // user subscription type is NOT TRIAL
                    return !userIsRO && !userIsTrial;
                } else {
                    return res.isPendingApproval;
                }
            });
        // return this.getListingByAssetId(metaData.creatorAssetId)
        //     .map(assetData => assetData.data.length === 0); // If data is present then it exists already
    }

    /**
     * Check if the asset listing can be upgraded
     * menuInput cannot be used for extensions as it is tenant specific menu
     * tenant must provide metadata to evaluate version comparision as TCI
     * application does not have version
     * @param opt
     * @param menuInput
     * @param metaData
     */
    canUpgradeComponentFunction(opt: ITCITenantComponentOptions<any>,
        menuInput: ITCITenantComponentInput,
        metaData: ITCIMarketplaceListingMetaData): Observable<boolean> {
        const assetObs = this.getListingByAssetId(metaData.creatorAssetId);
        const userInfoObs = this.userSvc.getUserInfo();
        return Observable.combineLatest(assetObs, userInfoObs, (assetData, userinfo) => ({
            assetData,
            userinfo
        }))
            .map(res => {
                if (res.assetData.data.length > 0) {
                    const providerGsbc = res.assetData.data[0].managingOrgId;
                    const sameOrg = res.userinfo.gsbc === providerGsbc; // listing belongs to user Org
                    const userIsAdmin = res.userinfo.rolX[0] === USER_ROLES.ADM; // only admin can update
                    const userIsRO = res.userinfo.rolX[0] === USER_ROLES.RO; // RO cannot edit
                    const userIsTrial = res.userinfo.st === USER_SUBSCRIPTION_TRIAL; // user is a trial user
                    const listingIsPendingApproval = res.assetData.data[0]
                        .listingOrgTypeStatusMap[TCI_MARKETPLACE_ORG_TYPE.Provider].equals(
                            TCIMarketplaceListingStatus.PENDING_APPROVAL);
                    const listingIsPublic = res.assetData.data[0]
                        .listingOrgTypeStatusMap[TCI_MARKETPLACE_ORG_TYPE.Provider].equals(
                            TCIMarketplaceListingStatus.PUBLICLY_AVAILABLE);
                    const metadataChanged = ((res.assetData.data[0].version !== metaData.version) || // version changed
                        (res.assetData.data[0].name !== metaData.name)); // name changed

                    if (listingIsPublic) { // visibility is public
                        // listing belongs to the same org as user
                        // the user is ADM role
                        // metadata has changed
                        // the user subscription type is not TRIAL
                        return sameOrg && userIsAdmin && metadataChanged && !userIsTrial;

                    } else { // visibility is private
                        // listing belongs to the same org as user
                        // the user role is NOT RO
                        // metadata has changed
                        // the user subscription type is not TRIAL
                        // listing is NOT in PendingApproval status
                        return sameOrg && metadataChanged && !userIsTrial && !userIsRO && !listingIsPendingApproval;
                    }

                }
                return false;
            });
    }

    /**
     * Check if the asset listing can be updated
     * menuInput cannot be used for extensions as it is tenant specific menu
     * tenant must provide metadata to evaluate version comparision as TCI
     * application does not have version
     * @param opt
     * @param menuInput
     * @param metaData
     */
    canUpdateComponentFunction(opt: ITCITenantComponentOptions<any>,
        menuInput: ITCITenantComponentInput,
        metaData: ITCIMarketplaceListingMetaData): Observable<boolean> {
        const assetObs = this.getListingByAssetId(metaData.creatorAssetId);
        const userInfoObs = this.userSvc.getUserInfo();
        return Observable.combineLatest(assetObs, userInfoObs, (assetData, userinfo) => ({
            assetData,
            userinfo
        }))
            .map(res => {

                if (res.assetData.data.length > 0) { // there has to be a listing to evaluate
                    const providerGsbc = res.assetData.data[0].managingOrgId;
                    const sameOrg = res.userinfo.gsbc === providerGsbc; // listing belongs to user Org
                    const userIsAdmin = res.userinfo.rolX[0] === USER_ROLES.ADM; // only admin can update
                    const userIsRO = res.userinfo.rolX[0] === USER_ROLES.RO; // only admin can update
                    const userIsTrial = res.userinfo.st === USER_SUBSCRIPTION_TRIAL; // user is a trial user
                    const listingIsPendingApproval = res.assetData.data[0]
                        .listingOrgTypeStatusMap[TCI_MARKETPLACE_ORG_TYPE.Provider].equals(
                            TCIMarketplaceListingStatus.PENDING_APPROVAL);
                    const listingIsPublic = res.assetData.data[0]
                        .listingOrgTypeStatusMap[TCI_MARKETPLACE_ORG_TYPE.Provider].equals(
                            TCIMarketplaceListingStatus.PUBLICLY_AVAILABLE);


                    if (listingIsPublic) { // public
                        // listing belongs the user Org
                        // listing is not a trial user
                        // user is ADM
                        return sameOrg && userIsAdmin && !userIsTrial;
                    } else {
                        // listing belongs to user Org
                        // user is NOT a trial user
                        // user is NOT a RO user
                        // listing is not in PendingApproval status
                        return sameOrg && !userIsTrial && !listingIsPendingApproval && !userIsRO;
                    }
                }
                return false;
            });
    }

    downloadAssets(listingId: string): Observable<{ [key: string]: Blob }> {
        return this.http.get(`${SERVICE_URLS.MARKETPLACE}/${listingId}${BASE_CONSTANTS.DOWNLOAD}`, {
                responseType: ResponseContentType.Blob
            })
            .map((res: Response) => {
                return {
                    [this.extractFileName(res.headers.get("Content-Disposition"), listingId)]: res.blob()
                };
            })
            .catch(err => {
                return Observable.throw(err);
            });
    }

    private extractFileName(contentDisposition: string, defaultName: string): string {
        const regex = /filename="([^)]+)"/;
        const results = contentDisposition.match(regex);

        return results[1] || defaultName;
    }
}
